import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const GENDER_OPTIONS = ["Any", "Woman", "Man", "Non-binary"];
const VIBE_TAG_OPTIONS = [
    "Night Owl", "Early Bird", "Flexible Sleeper",
    "Spotless", "Tidy", "Chill Clean", "Chaotic Good",
    "Party Animal", "Social Butterfly", "Balanced", "Homebody",
    "Home Chef", "Weekend Cook", "Takeout Fan", "Kitchen Ghost",
    "Pet Lover", "Pet Friendly", "No Pets", "Pet Allergic",
    "Host w/ the Most", "Sometimes Guests", "Rare Guests", "Solo Vibes",
];

export default function FilterBar({ filters, setFilters, showVibeTags = false, testIdPrefix = "filter" }) {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(filters);

    const open_ = () => {
        setDraft(filters);
        setOpen(true);
    };
    const apply = () => {
        setFilters(draft);
        setOpen(false);
    };
    const clear = () => {
        const empty = { city: "", budget_min: "", budget_max: "", move_in_after: "", gender: "Any", vibe_tags: [] };
        setDraft(empty);
        setFilters(empty);
        setOpen(false);
    };

    const activeCount = [
        filters.city,
        filters.budget_min,
        filters.budget_max,
        filters.move_in_after,
        filters.gender && filters.gender !== "Any" ? filters.gender : "",
        ...(filters.vibe_tags || []),
    ].filter(Boolean).length;

    return (
        <>
            <div className="flex items-center gap-2 flex-wrap" data-testid={`${testIdPrefix}-bar`}>
                <button
                    onClick={open_}
                    data-testid={`${testIdPrefix}-open-btn`}
                    className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-2 text-sm font-semibold lift"
                >
                    <SlidersHorizontal className="h-4 w-4" strokeWidth={2.5} />
                    Filters
                    {activeCount > 0 && (
                        <span className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-black grid place-items-center">
                            {activeCount}
                        </span>
                    )}
                </button>
                {filters.city && (
                    <FilterPill label={`📍 ${filters.city}`} onClear={() => setFilters({ ...filters, city: "" })} testId={`${testIdPrefix}-pill-city`} />
                )}
                {filters.budget_max && (
                    <FilterPill
                        label={`Up to ${filters.budget_max}`}
                        onClear={() => setFilters({ ...filters, budget_min: "", budget_max: "" })}
                        testId={`${testIdPrefix}-pill-budget`}
                    />
                )}
                {filters.gender && filters.gender !== "Any" && (
                    <FilterPill label={filters.gender} onClear={() => setFilters({ ...filters, gender: "Any" })} testId={`${testIdPrefix}-pill-gender`} />
                )}
                {(filters.vibe_tags || []).map((t) => (
                    <FilterPill
                        key={t}
                        label={t}
                        onClear={() =>
                            setFilters({ ...filters, vibe_tags: filters.vibe_tags.filter((x) => x !== t) })
                        }
                        testId={`${testIdPrefix}-pill-tag-${t}`}
                    />
                ))}
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[70] bg-background/70 backdrop-blur-xl"
                        onClick={() => setOpen(false)}
                        data-testid={`${testIdPrefix}-sheet`}
                    >
                        <motion.div
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            exit={{ y: "100%" }}
                            transition={{ type: "spring", stiffness: 260, damping: 30 }}
                            className="absolute inset-x-0 bottom-0 mx-auto max-w-xl rounded-t-4xl border-t border-border bg-card p-6 pb-8"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <div className="font-display text-2xl font-black">Filters</div>
                                <button onClick={() => setOpen(false)} className="h-9 w-9 rounded-full hover:bg-accent grid place-items-center" data-testid={`${testIdPrefix}-close-btn`}>
                                    <X className="h-5 w-5" strokeWidth={2.5} />
                                </button>
                            </div>
                            <div className="space-y-4 max-h-[70vh] overflow-y-auto no-scrollbar">
                                <div>
                                    <Label className="text-sm font-semibold">City</Label>
                                    <Input
                                        value={draft.city}
                                        onChange={(e) => setDraft({ ...draft, city: e.target.value })}
                                        placeholder="Mumbai, Bengaluru, Delhi…"
                                        className="mt-2 h-12 rounded-2xl"
                                        data-testid={`${testIdPrefix}-city-input`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-sm font-semibold">Budget range</Label>
                                    <div className="mt-2 grid grid-cols-2 gap-2">
                                        <Input
                                            type="number"
                                            min={0}
                                            value={draft.budget_min}
                                            onChange={(e) => setDraft({ ...draft, budget_min: e.target.value })}
                                            placeholder="Min"
                                            className="h-12 rounded-2xl"
                                            data-testid={`${testIdPrefix}-budget-min-input`}
                                        />
                                        <Input
                                            type="number"
                                            min={0}
                                            value={draft.budget_max}
                                            onChange={(e) => setDraft({ ...draft, budget_max: e.target.value })}
                                            placeholder="Max"
                                            className="h-12 rounded-2xl"
                                            data-testid={`${testIdPrefix}-budget-max-input`}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-sm font-semibold">Move-in after</Label>
                                    <Input
                                        type="date"
                                        value={draft.move_in_after}
                                        onChange={(e) => setDraft({ ...draft, move_in_after: e.target.value })}
                                        className="mt-2 h-12 rounded-2xl"
                                        data-testid={`${testIdPrefix}-movein-input`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-sm font-semibold">Gender</Label>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {GENDER_OPTIONS.map((g) => (
                                            <button
                                                key={g}
                                                type="button"
                                                onClick={() => setDraft({ ...draft, gender: g })}
                                                data-testid={`${testIdPrefix}-gender-${g.toLowerCase().replace(/[^a-z]/g, "-")}`}
                                                className={`rounded-full px-4 py-2 text-sm font-medium lift ${
                                                    draft.gender === g
                                                        ? "bg-primary text-primary-foreground"
                                                        : "border border-border bg-background hover:bg-accent"
                                                }`}
                                            >
                                                {g}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                {showVibeTags && (
                                    <div>
                                        <Label className="text-sm font-semibold">Vibe tags</Label>
                                        <p className="text-xs text-muted-foreground mt-0.5">Match any of the selected</p>
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {VIBE_TAG_OPTIONS.map((t) => {
                                                const active = (draft.vibe_tags || []).includes(t);
                                                return (
                                                    <button
                                                        key={t}
                                                        type="button"
                                                        onClick={() =>
                                                            setDraft({
                                                                ...draft,
                                                                vibe_tags: active
                                                                    ? draft.vibe_tags.filter((x) => x !== t)
                                                                    : [...(draft.vibe_tags || []), t],
                                                            })
                                                        }
                                                        data-testid={`${testIdPrefix}-tag-${t}`}
                                                        className={`rounded-full px-3 py-1.5 text-xs font-semibold lift ${
                                                            active
                                                                ? "bg-primary text-primary-foreground"
                                                                : "border border-border bg-background hover:bg-accent"
                                                        }`}
                                                    >
                                                        {t}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="mt-6 grid grid-cols-2 gap-2">
                                <Button variant="ghost" onClick={clear} data-testid={`${testIdPrefix}-clear-btn`} className="h-12 rounded-2xl font-semibold">
                                    Clear
                                </Button>
                                <Button onClick={apply} data-testid={`${testIdPrefix}-apply-btn`} className="h-12 rounded-2xl font-semibold lift">
                                    Apply
                                </Button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

function FilterPill({ label, onClear, testId }) {
    return (
        <span className="inline-flex items-center gap-1 rounded-full bg-accent text-accent-foreground px-3 py-1.5 text-xs font-semibold" data-testid={testId}>
            {label}
            <button onClick={onClear} className="hover:opacity-70" aria-label="clear">
                <X className="h-3 w-3" strokeWidth={3} />
            </button>
        </span>
    );
}
